import { useDimensions } from "@/hooks/useDimensions";

/**
 * What the app is on, and how much room it has: two questions, answered apart.
 *
 * ``isMobile`` is the device - a phone or a tablet, touched rather than
 * pointed at. Chromium says so (``userAgentData``); Safari and Firefox name
 * the device in their user agent; and an iPad, which names a Mac there, is
 * known by its pointer: a finger, with nothing to hover. It decides how a
 * thing is touched: a sheet rather than a popper, an icon alone rather than
 * its word, no key hints, a thumb's height.
 *
 * ``isNarrow`` is the room: a screen or window no wider than the theme's
 * ``md``, a phone's or a computer's alike. It decides what fits: one pane at
 * a time, the tab bar for the sidebar, a panel over the page rather than
 * beside it, a dialog the whole of it. ``isWide`` is room past ``lg``.
 *
 * A phone is both. A narrow window on a computer is narrow alone: laid out
 * as a phone is, it keeps its keys, their hints and its poppers.
 */
const useDevice = () => {
  const { width } = useDimensions();

  if (typeof window === "undefined") {
    return { isMobile: false, isNarrow: false, isWide: false };
  }

  const isMobile =
    navigator["userAgentData"]?.mobile ||
    /Android|iPhone|iPad|iPod|Mobile/.test(navigator.userAgent) ||
    window.matchMedia("(pointer: coarse) and (hover: none)").matches;

  return {
    isMobile,
    isNarrow: width <= 720,
    isWide: width > 1000,
  };
};

export { useDevice };
